import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { TaskPatientSearchProps } from './TaskPatientSearch.model'
import { LightPatient } from '../../../../../../model/Patient'
import { Icon, SelectSearch, TooltipWrapper } from '../../../../../shared'
import { getDisplayedName } from '../../../../../../misc/patient.utilities'
import { Link } from 'react-router-dom'
import { isEqual } from 'lodash'
import { useGetInfinitePatient } from '../../../../../../hooks/queries/patients'
import { useDebounce } from 'react-use'
import { SelectOption } from '../../../../../../model/SelectOption'
import styles from './TaskPatientSearch.module.scss'

const PATIENT_FILTERS_INITIAL_STATE = { search: '' }

export const TaskPatientSearch: FunctionComponent<TaskPatientSearchProps> = ({
  selectedPatient,
  setSelectedPatient,
  isEditModeEnabled,
}) => {
  const [patientSearchText, setPatientSearchText] = useState('')
  const [patientFilters, setPatientFilters] = useState(PATIENT_FILTERS_INITIAL_STATE)
  const [patients, setPatients] = useState<LightPatient[]>([])
  const {
    query: { isLoading },
    patientList,
  } = useGetInfinitePatient({
    enabled: !isEqual(patientFilters, PATIENT_FILTERS_INITIAL_STATE),
    filters: patientFilters,
  })
  const displayedName = selectedPatient ? getDisplayedName(selectedPatient) : ''
  const patientDefaultName = !displayedName
    ? 'Aucun patient'
    : `${displayedName.firstname} ${displayedName.lastname}`

  useDebounce(
    () => {
      setPatientFilters((prev) => {
        return patientSearchText.length >= 3
          ? { ...prev, search: patientSearchText }
          : { ...prev, search: PATIENT_FILTERS_INITIAL_STATE.search }
      })
    },
    500,
    [setPatientFilters, patientSearchText],
  )

  useEffect(() => {
    if (!isLoading) {
      setPatients(patientList)
    }
  }, [patientList, isLoading])

  const selectedPatientOption = selectedPatient
    ? { value: selectedPatient, label: selectedPatient.identity }
    : undefined

  const patientOptions = useMemo(
    () =>
      !isEqual(patientFilters, PATIENT_FILTERS_INITIAL_STATE)
        ? patients.map((patient) => ({
            value: patient,
            label: patient.identity,
          }))
        : [],
    [patientFilters, patients],
  )

  const onSelectPatientOption = (patientOption?: SelectOption<LightPatient>) => {
    if (patientOption) {
      const displayedName = patientOption.value ? getDisplayedName(patientOption.value) : null
      setSelectedPatient(patientOption.value)
      setPatientSearchText(`${displayedName?.firstname} ${displayedName?.lastname}`)
    } else {
      setSelectedPatient()
      setPatientSearchText(PATIENT_FILTERS_INITIAL_STATE.search)
    }
  }

  return (
    <div className={styles.taskPatientSearchContainer}>
      {isEditModeEnabled ? (
        <SelectSearch<LightPatient>
          placeholder="Rechercher un patient"
          textValue={patientSearchText}
          onTextChange={(value) => {
            setPatientSearchText(value)
          }}
          value={selectedPatientOption}
          options={patientOptions}
          onSelect={onSelectPatientOption}
          clearable
          testId="select_search-tasks-patient-search"
        />
      ) : (
        <div className={styles.taskSelectedPatientContainer}>
          {selectedPatient && selectedPatient.id ? (
            selectedPatient.deletedAt === null ? (
              <>
                <TooltipWrapper content="Naviguer vers le patient">
                  <Link to={`/patients/${selectedPatient?.id}`}>
                    <span className={`${styles.taskSelectedPatientName} ${styles.navigateTo}`}>
                      {patientDefaultName}
                    </span>
                  </Link>
                </TooltipWrapper>
              </>
            ) : (
              <>
                <TooltipWrapper content="Patient·e supprimé·e">
                  <span className={styles.taskSelectedPatientName}>{patientDefaultName}</span>
                </TooltipWrapper>
              </>
            )
          ) : (
            <>
              <span className={styles.taskSelectedPatientName}>{patientDefaultName}</span>
            </>
          )}
          <div className={styles.taskSelectedPatientChevronIcon}>
            <Icon size="femto" icon="chevron" />
          </div>
        </div>
      )}
    </div>
  )
}
